import { useState, useEffect, useMemo } from "react";
import { Button, Table, Tag, Space, Popover, message, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  AddArmCohortModal,
  ConfirmModal,
  FinalizateBar,
  FinalizateButton,
  Loading,
} from "src/DigitalProtocolModule/components";
import threeDotsIcon from "src/assets/svgs/icon-three-dots.svg";
import {
  deleteArmById,
  getDpArmList,
} from "src/DigitalProtocolModule/utils/ajax-proxy";
import { useSelector } from "react-redux";
import { fetchHistory } from "src/actions/historyTrial";
import styles from "./ArmsAndCohorts.module.scss";
import "./ArmsAndCohorts.scss";

export default function ArmsAndCohorts() {
  const trial_uuid = localStorage.getItem("trial-id");

  const finalizeStatus = useSelector(fetchHistory).data?.dpFinalizeStatus;

  const isLocked = useMemo(
    () => Boolean(finalizeStatus?.arm_cohort_finalize_flag),
    [finalizeStatus]
  );

  const columns: ColumnsType<Digital_Protocol_Arm_Types.Arm> = useMemo(() => {
    const tmpColumns = [
      {
        title: "Arm",
        dataIndex: "arm_title",
        key: "arm_title",
        width: "40%",
        render: (_, { arm_title, arm_description }) => (
          <>
            <div className={styles.nameTxt}>{arm_title}</div>
            <Tooltip title={arm_description}>
              <div className={styles.descTxt}>
                {arm_description
                  ? arm_description.length > 100
                    ? `${arm_description.substring(0, 100)}...`
                    : arm_description
                  : '-'}
              </div>
            </Tooltip>
          </>
        ),
      },
      {
        title: "Cohort",
        dataIndex: "cohorts",
        key: "cohorts",
        render: (_, { cohorts }) => (
          <div className={styles.cohortWrapper}>
            {(cohorts || []).map((item) => {
              return (
                <Tag className={styles.cohortTag} key={item.cohort_id}>
                  {item.cohort_name}
                </Tag>
              );
            })}
          </div>
        ),
      },
    ];
    if (isLocked) {
      return tmpColumns;
    } else {
      return [
        ...tmpColumns,
        {
          title: "",
          key: "",
          width: 60,
          render: (_, record) => (
            <Space size="middle">
              <Popover
                placement="bottomRight"
                trigger="click"
                className={styles.threeDots}
                content={() => (
                  <div className={styles.tableActions}>
                    <div
                      className={styles.edit}
                      onClick={() => handleEditArm(record)}
                    >
                      <EditOutlined />
                      Edit
                    </div>
                    <div
                      className={styles.delete}
                      onClick={() =>
                        setIsShowDelConfim({
                          visible: true,
                          arm_id: record.arm_id,
                          arm_title: record.arm_title,
                        })
                      }
                    >
                      <DeleteOutlined />
                      Delete
                    </div>
                  </div>
                )}
              >
                <img src={threeDotsIcon} alt="actions" />
              </Popover>
            </Space>
          ),
        },
      ];
    }
  }, [isLocked]);

  const [isLoading, setIsLoading] = useState(false);
  const [armList, setArmList] = useState<Digital_Protocol_Arm_Types.Arm[]>([]);
  const [isShowFinalize, setIsShowFinalize] = useState(false);
  const [isShowCreateModal, setIsShowCreateModal] =
    useState<Digital_Protocol_Arm_Types.AddModalParam>({
      visible: false,
    });
  const [isShowDelConfim, setIsShowDelConfim] =
    useState<Digital_Protocol_Arm_Types.DeleteConfirmType>({
      visible: false,
    });

  const fetchData = () => {
    setIsLoading(true);
    getDpArmList(trial_uuid)
      .then((res) => {
        setArmList(
          res.map((item: Digital_Protocol_Arm_Types.Arm) => ({
            ...item,
            cohorts: item.cohorts.map((cItem) => ({
              ...cItem,
              del_flg: false,
            })),
          }))
        );
      })
      .catch((e) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAddArm = () => {
    setIsShowCreateModal({ visible: true });
  };

  const handleEditArm = (data: Digital_Protocol_Arm_Types.Arm) => {
    setIsShowCreateModal({
      visible: true,
      armData: data,
    });
  };

  const handleDeleteArm = (arm_id: number) => {
    setIsLoading(true);
    deleteArmById({
      arm_id,
    })
      .then((res) => {
        if (res?.arm_id) {
          message.success(`Arm deleted successfully.`);
          fetchData();
        } else {
          message.error("Failed to delete.");
          setIsLoading(false);
        }
      })
      .catch((e) => {
        message.error("Failed to delete.");
        setIsLoading(false);
      })
      .finally(() => {});
  };

  const refreshAndCloseEditModal = () => {
    fetchData();
    setIsShowCreateModal({ visible: false });
  };

  const footer = () => (
    <div className={styles.tableFooter}>
      <Button
        className={styles.linkBtn}
        key="add_arm"
        type="text"
        size="small"
        onClick={handleAddArm}
      >
        ADD ARM
      </Button>
    </div>
  );

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {isLocked && (
        <div className={styles.bar}>
          <FinalizateBar category="arms" />
        </div>
      )}
      <div className={styles.header}>
        <span className={styles.title}>Arms & Cohorts</span>
        <div className={styles.btns}>
          {!isLocked && <FinalizateButton category="arms" />}
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <Loading isLoading={isLoading}>
          <Table
            dataSource={armList}
            columns={columns}
            rowKey="arm_id"
            className="dp-arm-table"
            footer={isLocked ? undefined : footer}
          />
        </Loading>
      </div>

      {isShowCreateModal.visible && (
        <AddArmCohortModal
          visible={isShowCreateModal.visible}
          armData={isShowCreateModal.armData}
          doCancel={() => setIsShowCreateModal({ visible: false })}
          afterSubmit={refreshAndCloseEditModal}
        />
      )}

      {isShowDelConfim?.visible && (
        <ConfirmModal
          visible={isShowDelConfim?.visible}
          title={`Delete Arm`}
          content={`This action will delete ${isShowDelConfim?.arm_title} and its corresponding cohort details.\nAre you sure you want to delete?`}
          handleCancel={() => setIsShowDelConfim({ visible: false })}
          handleConfirm={() => {
            handleDeleteArm(isShowDelConfim?.arm_id);
            setIsShowDelConfim({ visible: false });
          }}
        />
      )}
    </>
  );
}
